import React, { useState, useRef, useEffect, useMemo, useCallback, forwardRef, useImperativeHandle } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Typography,
  Backdrop,
  CircularProgress,
  IconButton,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  LinearProgress,
  Alert,
  AlertTitle,
  Tooltip,
  useMediaQuery
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import ProjectOrderSuccess from "./ProjectOrderSuccess";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import OAuthService from "src/app/auth/services/oAuthService";
import oAuthServiceConfig from "src/app/auth/services/oAuthService/oAuthServiceConfig";
import FormPageHeader from "../../utilities/FormPageHeader";
import { toast } from "react-toastify";
import { AgGridReact } from "ag-grid-react";
import { styled, useTheme } from "@mui/material/styles";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import customStyles from "app/shared-components/SelectCustomStyle";
import Select from "react-select";
import "ag-grid-enterprise";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.min.css";

const uploadUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.uploadPicture}`;
const uploadOrderFileUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.uploadOrderFile}`;
const newPrjOrderUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.newCustomerProjectOrder}`;
const rangeUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.findRanges}`;
const colorUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.ranges}`;
const handleUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.findAllHandlesByVendor}`;
const carcassUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.findAllCarcassByVendor}`;
const aoRoReasonsUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.aoRoReasons}`;

const hasSubReasons = (row) => Array.isArray(row?.subReasons) && row.subReasons.length > 0;
const isSubCategoryMissing = (row) => hasSubReasons(row) && (!row.subCategory || String(row.subCategory).trim() === "");

const ColorButton = styled(Button)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  backgroundColor: theme.palette.secondary.main,
  padding: "6px 16px",
  borderRadius: "20px",
  cursor: "pointer",
  display: "block",
  textAlign: "center",
  position: "relative",
  fontSize: "13px",
  fontWeight: 500,
  "&:hover": {
    backgroundColor: theme.palette.secondary.dark,
  },
  "&[disabled]": {
    opacity: 0.4,
    background: theme.palette.action.disabledBackground,
    color: theme.palette.text.disabled,
    "& .file": {
      cursor: "not-allowed",
    },
  },
  "& .file": {
    opacity: 0,
    width: "100%",
    height: "100%",
    position: "absolute",
    top: 0,
    left: 0,
    cursor: "pointer",
  },
}));

const ErrorCellValue = ({ value, hasError }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "4px", height: "100%" }}>
    {hasError && <WarningAmberOutlinedIcon sx={{ fontSize: 14, color: "error.main", flexShrink: 0 }} />}
    <span>{value}</span>
  </Box>
);

const GetReason = forwardRef((props, ref) => {
  const { data, colDef, reasonsList } = props;
  const refReasons = useRef();
  const [selectedReasons, setSelectedReasons] = useState(
    data && data.reasonId
      ? {
        value: data.reasonId,
        label: data.reason,
        imgUpldReqd: data.imgUpldReqd,
        postnNumReqd: data.postnNumReqd,
      }
      : null
  );

  const handleChangeReason = (option) => {
    if (!option) return;
    if (colDef.field === "reason") {
      const newData = {
        ...data,
        reason: option.label,
        reasonId: option.value,
        imgUpldReqd: option.imgUpldReqd,
        postnNumReqd: option.postnNumReqd,
        subReasons: option.subReasons || [],
        subCategory: option.value === data.reasonId ? (data.subCategory || "") : "",
      };
      props.node.setData(newData);
      if (props.onReasonChange) props.onReasonChange(props.node);
      setSelectedReasons(option);

      const node = props.node;
      const api = props.api;
      setTimeout(() => {
        api?.stopEditing();
        api?.redrawRows({ rowNodes: [node] });
      }, 0);
      return;
    }
    setSelectedReasons(option);
  };

  useImperativeHandle(ref, () => ({
    getValue() { return selectedReasons?.label; },
  }));

  return (
    <Box className="w-full">
      <Select ref={refReasons} value={selectedReasons} options={reasonsList} onChange={handleChangeReason} styles={{ ...customStyles(), menuPortal: (base) => ({ ...base, zIndex: 9999 }), menu: (base) => ({ ...base, width: "max-content", minWidth: "100%" }) }} menuPortalTarget={document.body} menuPosition="fixed" menuPlacement="auto" maxMenuHeight={200} className="w-full" />
    </Box>
  );
});

const GetSubCategory = forwardRef((props, ref) => {
  const { data } = props;
  const options = (data?.subReasons || []).map((item) => ({ value: item, label: item }));
  const initial = data?.subCategory ? { value: data.subCategory, label: data.subCategory } : null;
  const [selected, setSelected] = useState(initial);
  const selectedRef = useRef(initial);

  const handleChange = (option) => {
    if (!option) return;
    selectedRef.current = option;
    setSelected(option);
    setTimeout(() => props.api?.stopEditing(), 0);
  };

  useImperativeHandle(ref, () => ({
    getValue() { return selectedRef.current?.value ?? ""; },
  }));

  return (
    <Box className="w-full">
      <Select value={selected} options={options} onChange={handleChange} styles={{ ...customStyles(), menuPortal: (base) => ({ ...base, zIndex: 9999 }), menu: (base) => ({ ...base, width: "max-content", minWidth: "100%" }) }} menuPortalTarget={document.body} menuPosition="fixed" menuPlacement="auto" maxMenuHeight={200} className="w-full" />
    </Box>
  );
});

const PictureUploadCell = forwardRef((props, ref) => {
  const theme = useTheme();
  const { data, node } = props;
  const [pictureFile, setPictureFile] = useState(data?.picture || "");
  const [uploading, setUploading] = useState(false);

  if (!node || node.group || !data) return null;
  if (!data?.imgUpldReqd) return <Typography variant="caption" sx={{ color: theme.palette.text.disabled }}>N/A</Typography>;

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 15000000) {
      toast.error("File must be less than 15MB");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await OAuthService.uploadFile(uploadUrl, formData);
      const filename = response.data.fileName;
      setPictureFile(filename);
      node.setDataValue("picture", filename);
      props.api?.redrawRows({ rowNodes: [node] });
    } catch (error) {
      toast.error("Failed to upload file");
    } finally {
      setUploading(false);
    }
  };

  const removePicture = () => {
    setPictureFile("");
    node.setDataValue("picture", "");
    props.api?.redrawRows({ rowNodes: [node] });
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', height: '100%', width: '100%' }}>
      {!pictureFile ? (
        <>
          <WarningAmberOutlinedIcon sx={{ fontSize: 14, color: "error.main", flexShrink: 0 }} />
          <IconButton component="label" color="primary" disabled={uploading} size="small" aria-label="Upload mandatory picture" sx={{ padding: '0px' }}>
            {uploading ? <CircularProgress size={14} color="inherit" /> : <FuseSvgIcon size={18}>heroicons-outline:upload</FuseSvgIcon>}
            <input type="file" hidden onChange={handleUpload} />
          </IconButton>
        </>
      ) : (
        <Chip label={pictureFile} onDelete={removePicture} size="small" sx={{ maxWidth: '100px', height: '18px', fontSize: '10px' }} />
      )}
    </Box>
  );
});

export default function BulkPartOrderSummary() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
  const location = useLocation();
  const navigate = useNavigate();
  const gridRef = useRef(null);
  const validationTimer = useRef(null);

  const [globalData, setGlobalData] = useState(null);
  const [parsedOrders, setParsedOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [key, setKey] = useState(0);
  const [validationTrigger, setValidationTrigger] = useState(0);
  const [expanded, setExpanded] = useState("panel0");

  const [sharedLater, setSharedLater] = useState(false);
  const [notApplicable, setNotApplicable] = useState(false);
  const [drwFileSupersedes, setDrwFileSupersedes] = useState(false);

  const [formValues, setFormValues] = useState([{ range: {}, priceGroup: {}, color: {}, carcass: {}, handle: {} }]);
  const [range, setRange] = useState([]);
  const [color, setColor] = useState([]);
  const [carcass, setCarcass] = useState([]);
  const [handles, setHandles] = useState([]);
  const [reasonOptions, setReasonOptions] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitError, setHasSubmitError] = useState(false);
  const [confirmResubmitOpen, setConfirmResubmitOpen] = useState(false);
  const [openSuccessOrder, setOpenSuccessOrder] = useState(false);
  const [currentOrder, setCurrentOrder] = useState([]);
  const [progressStats, setProgressStats] = useState({ total: 0, completed: 0, successful: 0, failed: 0 });
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [submissionResults, setSubmissionResults] = useState([]);
  const [gridApi, setGridApi] = useState(null);

  useEffect(() => {
    if (location.state?.parsedOrders && location.state?.globalData) {
      setParsedOrders(location.state.parsedOrders);
      setGlobalData(location.state.globalData);
      navigate(".", { replace: true, state: null });
    } else if (!parsedOrders.length) {
      navigate("/order/bulk-part-order", { replace: true });
    }
  }, [location, navigate, parsedOrders]);

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const reasonRes = await OAuthService.getList(`${aoRoReasonsUrl}?size=1000`);
        setReasonOptions(reasonRes.data?._embedded?.aoRoReasons?.map((item) => ({
          value: item.reasonId,
          label: item.reason,
          imgUpldReqd: item.imgUpldReqd,
          postnNumReqd: item.postnNumReqd,
          subReasons: item.subReasons || [],
        })) || []);

        if (globalData?.product?.id) {
          const [rangeRes, handleRes, carcassRes] = await Promise.all([
            OAuthService.getList(`${rangeUrl}?vendorId=${globalData.product.id}`),
            OAuthService.getList(`${handleUrl}?vendorId=${globalData.product.id}`),
            OAuthService.getList(`${carcassUrl}?vendorId=${globalData.product.id}`)
          ]);

          setRange(rangeRes.data?.map(r => ({ value: r.productId, label: `${r.priceGroup?.pgId || "NA"}-${r.productName}`, id: r.id, priceGroup: r.priceGroup?.pgId })));
          setHandles(handleRes.data?._embedded?.handles?.map(h => ({ value: h.productId, label: `${h.productId}-${h.productName}` })));
          setCarcass(carcassRes.data?._embedded?.carcasses?.map(c => ({ value: c.productId, label: `${c.productId}-${c.productName}` })));
        }
      } catch (error) {
        console.error("Failed to load initial summary dropdowns", error);
      }
    };
    if (globalData) fetchDropdownData();
  }, [globalData]);

  const handleRangeChange = (data, index, field) => {
    setFormValues((prev) => {
      const updated = prev.map((row, i) => {
        if (i !== index) return row;
        const newRow = { ...row, [field]: { value: data.value, label: data.label } };
        if (field === "range") {
          newRow.color = {};
          newRow.priceGroup = { value: data.priceGroup, label: data.priceGroup };
        }
        return newRow;
      });
      return updated;
    });

    if (field === "range") {
      OAuthService.getList(`${colorUrl}/${data.id}/variations`).then((list) => {
        const colors = list.data?._embedded?.rangeVariations?.map((v) => ({ value: v.productId, label: `${v.productId}-${v.productName}` }));
        setColor((prev) => { const updated = [...prev]; updated[index] = colors; return updated; });
      }).catch(console.error);
    }
  };

  const syncGridDataToParsedOrders = (orders) => {
    if (!gridApi) return orders;
    const currentGridData = {};
    gridApi.forEachLeafNode(node => {
      currentGridData[node.data.__rowId] = node.data;
    });

    return orders.map(kitchen => ({
      ...kitchen,
      partOrderDetails: kitchen.partOrderDetails.map((part, pIndex) => {
        const gridRow = currentGridData[`${kitchen.uniqueId}-${pIndex}`];
        return gridRow ? {
          ...part,
          reason: gridRow.reason,
          reasonId: gridRow.reasonId,
          subReasons: gridRow.subReasons,
          subCategory: gridRow.subCategory,
          articleCode: gridRow.articleCode,
          posNo: gridRow.posNo,
          remark: gridRow.remark,
          picture: gridRow.picture,
          imgUpldReqd: gridRow.imgUpldReqd,
          postnNumReqd: gridRow.postnNumReqd
        } : part;
      })
    }));
  };

  const handleApplyMaterialInfo = () => {
    gridApi?.stopEditing();
    if (!gridApi) return;
    setLoading(true);

    const selectedNodes = gridApi.getSelectedNodes();
    const selectedUniqueIds = new Set();
    selectedNodes.forEach(node => {
      if (node.data) selectedUniqueIds.add(node.data.uniqueId);
      else if (node.key) selectedUniqueIds.add(node.key);
    });

    if (!selectedUniqueIds.size) {
      toast.warning("Please select at least one row/kitchen in the grid");
      setLoading(false);
      return;
    }

    const selectedTypeNumbers = new Set();
    parsedOrders.forEach(kitchen => {
      if (selectedUniqueIds.has(kitchen.uniqueId)) {
        selectedTypeNumbers.add(kitchen.typeNumber || "1");
      }
    });

    setParsedOrders((prev) => {
      const synced = syncGridDataToParsedOrders(prev);
      return synced.map(kitchen => {
        if (selectedTypeNumbers.has(kitchen.typeNumber || "1")) {
          return {
            ...kitchen,
            range1: formValues[0].range?.label || "",
            range1Id: formValues[0].range?.value || "",
            priceGroup1: formValues[0].priceGroup?.value || "",
            color1: formValues[0].color?.label || "",
            color1Id: formValues[0].color?.value || "",
            carcass1: formValues[0].carcass?.label || "",
            carcass1Id: formValues[0].carcass?.value || "",
            handle1: formValues[0].handle?.label || "",
            handle1Id: formValues[0].handle?.value || "",
          };
        }
        return kitchen;
      });
    });

    setTimeout(() => {
      gridApi.redrawRows();
      setValidationTrigger(prev => prev + 1);
      setLoading(false);
      toast.success("Material info applied successfully!");
    }, 100);
  };

  const handleFileUpload = async (e, type) => {
     gridApi?.stopEditing();
    const file = e.target.files[0];
    if (!file) return;

    if (!gridApi) return;
    const selectedNodes = gridApi.getSelectedNodes();
    const selectedUniqueIds = new Set();

    selectedNodes.forEach(node => {
      if (node.data) selectedUniqueIds.add(node.data.uniqueId);
      else if (node.key) selectedUniqueIds.add(node.key);
    });

    if (selectedUniqueIds.size === 0) {
      toast.warning(`Please select at least one Kitchen row in the grid below to attach the ${type} file.`);
      setKey(prev => prev + 1);
      return;
    }

    if (type === 'DRW') {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      if (fileExtension !== "drw" && fileExtension !== "prw") {
        toast.warning("Please upload a file with .drw or .prw extension");
        setKey(prev => prev + 1);
        return;
      }
    }

    setLoading(true);
    try {
      let commissionName = "";
      gridApi.forEachLeafNode(node => {
        if (selectedUniqueIds.has(node.data.uniqueId) && !commissionName) {
          commissionName = node.data.commissionName;
        }
      });

      const payload = { file: [file], customerId: globalData.customerId, commissionName: commissionName || "ATTACHMENT", isOriginal: true };
      const res = await OAuthService.addFiles(uploadOrderFileUrl, payload);
      const fileId = res.data.map(f => f.id).join("");

      setParsedOrders(prev => {
        const synced = syncGridDataToParsedOrders(prev);
        return synced.map(kitchen => {
          if (selectedUniqueIds.has(kitchen.uniqueId)) {
            if (type === 'DRW') return { ...kitchen, drwFile: fileId, drwFileName: file.name };
            if (type === 'AD') return { ...kitchen, applianceDetail: fileId, applianceDetailName: file.name };
            if (type === 'CHECKLIST') return { ...kitchen, orderCheckList: fileId, orderCheckListName: file.name };
            if (type === 'DRAWING') {
              const currentIds = kitchen.drawingFile || [];
              const currentNames = kitchen.drawingFileNames || [];
              return { ...kitchen, drawingFile: [...currentIds, fileId], drawingFileNames: [...currentNames, file.name] };
            }
          }
          return kitchen;
        });
      });

      setTimeout(() => { gridApi.redrawRows(); }, 100);
      toast.success(`${type} File attached to selected orders!`);
    } catch (error) {
      toast.error(`Failed to upload ${type} File`);
    } finally {
      setLoading(false);
      setKey(prev => prev + 1);
    }
  };

  const handleRemoveFile = (type) => {
    if (!gridApi) return;
    const selectedNodes = gridApi.getSelectedNodes();
    const selectedUniqueIds = new Set();

    selectedNodes.forEach(node => {
      if (node.data) selectedUniqueIds.add(node.data.uniqueId);
      else if (node.key) selectedUniqueIds.add(node.key);
    });

    if (selectedUniqueIds.size === 0) {
      toast.warning(`Please select rows to remove the ${type} file.`);
      return;
    }

    setParsedOrders(prev => {
      const synced = syncGridDataToParsedOrders(prev);
      return synced.map(kitchen => {
        if (selectedUniqueIds.has(kitchen.uniqueId)) {
          if (type === 'DRW') return { ...kitchen, drwFile: "", drwFileName: "" };
          if (type === 'AD') return { ...kitchen, applianceDetail: "", applianceDetailName: "" };
          if (type === 'CHECKLIST') return { ...kitchen, orderCheckList: "", orderCheckListName: "" };
          if (type === 'DRAWING') return { ...kitchen, drawingFile: [], drawingFileNames: [] };
        }
        return kitchen;
      });
    });

    setTimeout(() => { gridApi.redrawRows(); }, 100);
    toast.success(`${type} removed from selected orders.`);
  };

  const gridData = useMemo(() => {
    const flatData = [];
    const uniqueTypes = [...new Set(parsedOrders.map(k => k.typeNumber || "1"))];
    const isOldFormat = globalData?.orderReference?.length === 6;
    parsedOrders.forEach((kitchen, kIndex) => {
      const rawType = kitchen.typeNumber || "1";
      const existingCount = globalData?.existingTypeCount || 0;
      const typeIndex = existingCount + uniqueTypes.indexOf(rawType) + 1;
      const formattedSeq = isOldFormat
        ? typeIndex.toString().padStart(2, '0')
        : typeIndex.toString().padStart(3, '0');
      const specificCommission = isOldFormat
        ? `${globalData.orderReference || ""}${formattedSeq}-${globalData.product?.value || ""}-${rawType}-${globalData.projectShortName || ""}-${kitchen.uniqueId || ""}`.toUpperCase()
        : `${globalData.orderReference || ""}${formattedSeq}${globalData.product?.value || ""}-${rawType}-${globalData.projectShortName || ""}-${kitchen.uniqueId || ""}`.toUpperCase();

      kitchen.partOrderDetails.forEach((part, pIndex) => {
        flatData.push({
          ...part,
          uniqueId: kitchen.uniqueId,
          commissionName: specificCommission,
          typeName: rawType,
          wg1: kitchen.wg1,
          wg2: kitchen.wg2,
          volume: kitchen.volume,
          weight: kitchen.weight,
          mappedRange: kitchen.range1Id ? kitchen.range1 : "Not Applied",
          mappedColor: kitchen.color1Id ? kitchen.color1 : "Not Applied",
          mappedCarcass: kitchen.carcass1Id ? kitchen.carcass1 : "Not Applied",
          mappedHandle: kitchen.handle1Id ? kitchen.handle1 : "Not Applied",
          drwFileName: kitchen.drwFileName || "",
          applianceDetailName: kitchen.applianceDetailName || "",
          orderCheckListName: kitchen.orderCheckListName || "",
          drawingFileNames: kitchen.drawingFileNames ? kitchen.drawingFileNames.join(", ") : "",
          __rowId: `${kitchen.uniqueId}-${pIndex}`
        });
      });
    });
    return flatData;
  }, [parsedOrders, globalData]);

  const columnDefs = useMemo(() => [
    { field: "typeName", headerName: "TYPE Name", rowGroup: true, hide: true },
    { field: "uniqueId", headerName: "Uniq ID", minWidth: 100 },
    { field: "commissionName", headerName: "Commission Name", minWidth: 260 },
    { field: "articleCode", headerName: "Article Code", minWidth: 150, editable: true, singleClickEdit: true },
    {
      field: "posNo", headerName: "Pos No", minWidth: 90, maxWidth: 100, editable: true, singleClickEdit: true,
      cellStyle: (params) => {
        if (!params || !params.data) return null;
        return (params.data.postnNumReqd && (!params.value || String(params.value).trim() === "")) ? { border: `1px solid ${theme.palette.error.main}` } : null;
      },
      cellRenderer: (params) => {
        if (!params.data) return null;
        return <ErrorCellValue value={params.value} hasError={!!(params.data.postnNumReqd && (!params.value || String(params.value).trim() === ""))} />;
      }
    },
    {
      field: "reason", headerName: "Reason", minWidth: 180, editable: true, cellEditorPopup: true, cellEditor: GetReason, singleClickEdit: true,
      cellEditorParams: {
        reasonsList: reasonOptions,
        onReasonChange: () => setValidationTrigger(prev => prev + 1)
      },
      cellStyle: (params) => {
        if (!params || !params.data) return null;
        return (!params.data.reasonId) ? { border: `1px solid ${theme.palette.error.main}` } : null;
      },
      cellRenderer: (params) => {
        if (!params.data) return null;
        return <ErrorCellValue value={params.value} hasError={!params.data.reasonId} />;
      }
    },
    {
      field: "subCategory", headerName: "Sub Category", minWidth: 180, singleClickEdit: true, cellEditorPopup: true, cellEditor: GetSubCategory,
      editable: (params) => hasSubReasons(params.data),
      cellStyle: (params) => {
        if (!params || !params.data) return null;
        return isSubCategoryMissing(params.data) ? { border: `1px solid ${theme.palette.error.main}` } : null;
      },
      cellRenderer: (params) => {
        if (!params.data) return null;
        if (!hasSubReasons(params.data)) return <Typography variant="caption" sx={{ color: theme.palette.text.disabled }}>N/A</Typography>;
        return <ErrorCellValue value={params.value} hasError={isSubCategoryMissing(params.data)} />;
      }
    },
    { field: "remark", headerName: "Remark", minWidth: 200, editable: true, singleClickEdit: true },
    { field: "wg1", headerName: "WG1", minWidth: 90 },
    { field: "wg2", headerName: "WG2", minWidth: 90 },
    { field: "volume", headerName: "Volume", minWidth: 90 },
    { field: "weight", headerName: "Weight", minWidth: 90 },
    { field: "mappedRange", headerName: "Mapped Range", minWidth: 150, cellStyle: (params) => params.value === "Not Applied" ? { color: theme.palette.error.main, border: `1px solid ${theme.palette.error.main}` } : null },
    { field: "mappedColor", headerName: "Mapped Color", minWidth: 150, cellStyle: (params) => params.value === "Not Applied" ? { color: theme.palette.error.main, border: `1px solid ${theme.palette.error.main}` } : null },
    { field: "mappedCarcass", headerName: "Mapped Carcass", minWidth: 150, cellStyle: (params) => params.value === "Not Applied" ? { color: theme.palette.error.main, border: `1px solid ${theme.palette.error.main}` } : null },
    { field: "mappedHandle", headerName: "Mapped Handle", minWidth: 150, cellStyle: (params) => params.value === "Not Applied" ? { color: theme.palette.error.main, border: `1px solid ${theme.palette.error.main}` } : null },
    { field: "drwFileName", headerName: "DRW File", minWidth: 150 },
    { field: "applianceDetailName", headerName: "Appliance Detail", minWidth: 150 },
    { field: "orderCheckListName", headerName: "Order Checklist", minWidth: 150 },
    { field: "drawingFileNames", headerName: "Drawing Files", minWidth: 150 },
    {
      field: "picture", headerName: "Mandatory Picture", minWidth: 180, cellRenderer: PictureUploadCell,
      cellStyle: (params) => {
        if (!params || !params.data) return null;
        return (params.data.imgUpldReqd && !params.value) ? { border: `1px solid ${theme.palette.error.main}` } : null;
      }
    }
  ], [theme, reasonOptions]);

  const autoGroupColumnDef = useMemo(() => ({
    headerName: "TYPE Name",
    minWidth: isMobile ? 180 : 250,
    pinned: isMobile ? undefined : 'left',
    headerCheckboxSelection: true,
    cellRendererParams: {
      checkbox: true,
      suppressCount: false
    },
  }), [isMobile]);

  const defaultColDef = useMemo(() => ({
    filter: true,
    floatingFilter: true,
    resizable: true,
    sortable: true
  }), []);

  const onGridReady = useCallback((params) => {
    setGridApi(params.api);
  }, []);

  const onSelectionChanged = useCallback(() => {
    const selectedNodes = gridRef.current?.api?.getSelectedNodes() || [];
    setSelectedRows(selectedNodes);
  }, []);

  const handleCellValueChanged = useCallback((event) => {
    if (event?.node) {
      event.api?.redrawRows({ rowNodes: [event.node] });
    }
    if (validationTimer.current) clearTimeout(validationTimer.current);
    validationTimer.current = setTimeout(() => {
      setValidationTrigger(prev => prev + 1);
    }, 300);
  }, []);

  const handleRowDataUpdated = useCallback(() => {
    setValidationTrigger(prev => prev + 1);
  }, []);

  const validationStatus = useMemo(() => {
    let isMaterialDone = parsedOrders.length > 0 && parsedOrders.every(k => k.range1Id && k.color1Id && k.carcass1Id && k.handle1Id);
    let gridValid = true;
    let hasGridErrors = false;

    const kitchenIdsInGrid = new Set();

    if (gridApi) {
      gridApi.forEachLeafNode((node) => {
        if (node.data && node.data.uniqueId) kitchenIdsInGrid.add(node.data.uniqueId);
        if (!node.data) return;

        if (!node.data.reasonId) { gridValid = false; hasGridErrors = true; }
        if (isSubCategoryMissing(node.data)) { gridValid = false; hasGridErrors = true; }
        if (node.data.postnNumReqd && (!node.data.posNo || String(node.data.posNo).trim() === "")) { gridValid = false; hasGridErrors = true; }
        if (node.data.imgUpldReqd && !node.data.picture) { gridValid = false; hasGridErrors = true; }
      });
    } else {
      gridValid = false;
      hasGridErrors = true;
    }

    if (parsedOrders.some(k => !kitchenIdsInGrid.has(k.uniqueId))) gridValid = false;

    return {
      isMaterialCompleted: isMaterialDone,
      hasGridErrors: hasGridErrors,
      isValidationPassed: gridValid && isMaterialDone
    };
  }, [parsedOrders, validationTrigger, gridApi]);

  const handleAddPart = () => {
    if (!gridApi) return;
    const selectedNodes = gridApi.getSelectedNodes();

    if (!selectedNodes || selectedNodes.length === 0) {
      toast.warning("Please select a Kitchen row (or a part within it) to add a new part.");
      return;
    }

    const targetUniqueId = selectedNodes[0].data ? selectedNodes[0].data.uniqueId : selectedNodes[0].key;
    if (!targetUniqueId) return;

    setParsedOrders((prevOrders) => {
      const synced = syncGridDataToParsedOrders(prevOrders);
      return synced.map((kitchen) => {
        if (kitchen.uniqueId === targetUniqueId) {
          return {
            ...kitchen,
            partOrderDetails: [
              ...kitchen.partOrderDetails,
              { reason: "", reasonId: "", subReasons: [], subCategory: "", articleCode: "", posNo: "", remark: "", picture: "", imgUpldReqd: false, postnNumReqd: false }
            ]
          };
        }
        return kitchen;
      });
    });

    setTimeout(() => {
      gridApi.forEachNode((node) => {
        if (node.key === targetUniqueId && node.group) node.setExpanded(true);
      });
    }, 100);
  };

  const handleRemovePart = () => {
    if (!gridApi) return;
    const selectedNodes = gridApi.getSelectedNodes();
    const selectedLeafNodes = selectedNodes.filter(node => !node.group && node.data);

    if (selectedLeafNodes.length === 0) {
      toast.warning("Please select specific part rows to remove.");
      return;
    }

    const rowsToRemove = selectedLeafNodes.map(node => ({
      uniqueId: node.data.uniqueId,
      rowId: node.data.__rowId
    }));

    setParsedOrders((prevOrders) => {
      return prevOrders.map((kitchen) => {
        const rowsForThisKitchen = rowsToRemove.filter(r => r.uniqueId === kitchen.uniqueId);

        if (rowsForThisKitchen.length > 0) {
          if (kitchen.partOrderDetails.length === rowsForThisKitchen.length) {
            toast.error(`Cannot remove all parts for Kitchen ${kitchen.uniqueId}. At least one part must remain.`);
            return kitchen;
          }

          const indicesToRemove = rowsForThisKitchen.map(r => parseInt(r.rowId.split('-').pop()));
          return {
            ...kitchen,
            partOrderDetails: kitchen.partOrderDetails.filter((_, idx) => !indicesToRemove.includes(idx))
          };
        }
        return kitchen;
      });
    });

    gridApi.deselectAll();
  };

  const handlePlaceOrder = async () => {
    gridApi?.stopEditing();
    if (isSubmitting) return;
    setIsSubmitting(true);

    let hasHardErrors = false;
    gridApi?.forEachLeafNode((node) => {
      if (!node.data) return;
      if (!node.data.reasonId) hasHardErrors = true;
      if (isSubCategoryMissing(node.data)) hasHardErrors = true;
      if (node.data.postnNumReqd && (!node.data.posNo || String(node.data.posNo).trim() === "")) hasHardErrors = true;
      if (node.data.imgUpldReqd && !node.data.picture) hasHardErrors = true;
    });

    if (hasHardErrors) {
      toast.error("Validation failed. Please fix all red highlighted cells in the grid before submitting.");
      setIsSubmitting(false);
      return;
    }

    const negativeValueKitchens = parsedOrders.filter((kitchen) =>
      Number(kitchen.wg1) < 0 || Number(kitchen.wg2) < 0 || Number(kitchen.volume) < 0 || Number(kitchen.weight) < 0
    );
    if (negativeValueKitchens.length > 0) {
      toast.error(
        `Negative WG1/WG2/Volume/Weight values found for ${negativeValueKitchens.length} order(s) (e.g. Unique ID: ${negativeValueKitchens[0].uniqueId}). Please correct the source Excel and re-upload before submitting.`
      );
      setIsSubmitting(false);
      return;
    }

    try {
      const projectTypeInfoArr = [];
      const uniqueTypesForSubmit = [...new Set(parsedOrders.map(k => k.typeNumber || "1"))];

      parsedOrders.forEach((kitchen) => {
        const typeName = kitchen.typeNumber || "1";

        let kitchenSpecificCommission = "";
        const partsForKitchen = [];

        gridApi?.forEachLeafNode((node) => {
          if (node.data && node.data.uniqueId === kitchen.uniqueId) {
            kitchenSpecificCommission = node.data.commissionName;
            partsForKitchen.push({
              reason: node.data.reason || "",
              ...(node.data.subCategory ? { subReason: node.data.subCategory } : {}),
              posNo: String(node.data.posNo || ""),
              articleCode: node.data.articleCode || "",
              picture: node.data.picture || "",
              remark: node.data.remark || ""
            });
          }
        });

        if (partsForKitchen.length === 0) return;

        const existingCountSubmit = globalData?.existingTypeCount || 0;
        const typeSeqIndex = existingCountSubmit + uniqueTypesForSubmit.indexOf(typeName) + 1;
        projectTypeInfoArr.push({
          vendorId: globalData.product?.id || "",
          vendor: globalData.product?.label || "",
          projectTypeNum: typeSeqIndex,
          volume: Number(kitchen.volume || 0),
          weight: Number(kitchen.weight || 0),
          orderList: [{
            uniqId: String(kitchen.uniqueId || ""),
            commissionName: kitchenSpecificCommission
          }],
          partOrderDetails: partsForKitchen,
          range1Id: kitchen.range1Id || "",
          range1: kitchen.range1 || "",
          priceGroup1: kitchen.priceGroup1 || "",
          color1Id: kitchen.color1Id || "",
          color1: kitchen.color1 || "",
          carcass1Id: kitchen.carcass1Id || "",
          carcass1: kitchen.carcass1 || "",
          handle1Id: kitchen.handle1Id || "",
          handle1: kitchen.handle1 || "",
          wg1: Number(kitchen.wg1 || 0),
          wg2: Number(kitchen.wg2 || 0),
          isSpecialDiscount: false,
          loadingWeek: globalData.loadingWeek?.value ?? null,
          freightId: globalData.freight?.id || "",
          freight: globalData.freight?.label || "",
          specialComments: globalData.specialComments || "",
          sharedLater: sharedLater || false,
          na: notApplicable || false,
          drawFileSupersedes: drwFileSupersedes || false,
          drwFile: kitchen.drwFile || "",
          applianceDetail: kitchen.applianceDetail || null,
          orderCheckList: kitchen.orderCheckList || null,
          drawingFile: kitchen.drawingFile || [],
          typeName: typeName
        });
      });

      const projectTypeInfo = projectTypeInfoArr;

      if (projectTypeInfo.length === 0) {
        toast.warning("No valid part orders to submit.");
        setIsSubmitting(false);
        return;
      }

      const phasesInfo = (globalData.phasesData || []).map((row) => {
        const weekString = row.expectedProductionWeek;
        const week = weekString && typeof weekString === "string" ? weekString.split("/")[0] : weekString;
        const isCurrentPhase = (globalData.currentPhaseId === row.id || globalData.currentPhaseId === row.phaseName) ? true : false;
        const finalId = typeof row.id === "string" && row.id.startsWith("new-phase") ? null : row.id;

        let result = {
          id: finalId,
          phaseName: row.phaseName,
          qty: row.phaseViseQty,
          year: row.year,
          expectedProductionWeek: Number(week),
          phaseViseQty: row.phaseViseQty,
          isCurrentPhase: isCurrentPhase
        };

        if (isCurrentPhase) {
          const typeViseQty = {};
          projectTypeInfo.forEach(type => {
            typeViseQty[type.typeName] = (typeViseQty[type.typeName] || 0) + type.orderList.length;
          });
          result = { ...result, typeViseQty };
        } else if (row.orderPlaced && !isCurrentPhase) {
          result = { ...result, typeViseQty: row.typeViseQty || {} };
        }

        return result;
      });
      const finalCurrentPhaseId = typeof globalData.currentPhaseId === "string" && globalData.currentPhaseId.startsWith("new-phase") ? null : globalData.currentPhaseId;

      const payload = {
        customerId: globalData.customerId,
        showroomId: globalData.showroomId,
        orderCategory: "PRJ",
        docType: "AO",
        orderRef: globalData.orderReference,
        projectTypeInfo,
        overallQty:
          globalData.phasesData?.reduce(
            (sum, phase) => sum + Number(phase.phaseViseQty || 0),
            0
          ) || 0,
        projectDisplayName: globalData.projectShortName || "AO",
        phasesInfo,
        currentPhaseId: finalCurrentPhaseId,
        containerNo: globalData.containerNo || "",
        customerProjectNum: 0,
        projectName: globalData.projectName || ""
      };

      try {
        const list = await OAuthService.addData(newPrjOrderUrl, payload);
        const responseData = list.data;
        toast.success("Bulk Part Order submitted successfully!");
        setCurrentOrder(responseData);
        setOpenSuccessOrder(true);
        setHasSubmitError(false);
      } catch (innerError) {
        console.error("Submission Error:", innerError);
        setHasSubmitError(true);
        if (innerError?.response?.status >= 400 && innerError?.response?.status < 500) {
          toast.error(innerError?.response?.data?.errorMessage || "Validation failed. Please try again.");
          setIsSubmitting(false);
        } else if (innerError?.response?.status >= 500) {
          toast.error("Internal Server Error: Order failed to save due to a backend issue. Please try again later or contact support.");
          setIsSubmitting(false);
        } else {
          toast.error("Network error. Please check your connection and try again.");
          setIsSubmitting(false);
        }
      }

    } catch (error) {
      console.error(error);
      toast.error("Validation or payload construction failed.");
      setIsSubmitting(false);
    }
  };

  const closeResultDialog = () => {
    setShowResultDialog(false);
  };

  const handleSubmitClick = () => {
    if (hasSubmitError) {
      setConfirmResubmitOpen(true);
    } else {
      handlePlaceOrder();
    }
  };

  const handleConfirmResubmit = () => {
    setConfirmResubmitOpen(false);
    handlePlaceOrder();
  };

  const renderHeaderAction = () => {
    return (
      <Box display="flex" gap={{ xs: 1, sm: 2 }} alignItems="center">
        <Tooltip title="Go back">
          <span>
            <IconButton
              onClick={() => navigate(-1)}
              disabled={isSubmitting || loading}
              size="small"
              aria-label="Go back"
              sx={{ border: `1px solid ${theme.palette.divider}`, color: "inherit", padding: '4px' }}
            >
              <FuseSvgIcon size={20}>heroicons-outline:arrow-narrow-left</FuseSvgIcon>
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={!isSubmitting && !loading && !validationStatus.isValidationPassed ? "Fix highlighted grid errors and apply Material Details to all kitchens to enable submit." : ""}>
          <span>
            <Button
              variant="contained"
              color="secondary"
              size={isMobile ? "small" : "medium"}
              onClick={handleSubmitClick}
              disabled={isSubmitting || loading || !validationStatus.isValidationPassed}
            >
              {isSubmitting ? "Submitting..." : parsedOrders.length > 0 ? "Submit All Orders" : "No Orders Remaining"}
            </Button>
          </span>
        </Tooltip>
      </Box>
    );
  };

  if (!globalData) return null;

  return (
    <>
      <FormPageHeader componentName="Bulk Part Order Review" pageName="Bulk Part Order Review" orderHeaderAction={renderHeaderAction()} />

      {hasSubmitError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Previous submission failed</AlertTitle>
          Some valid orders may have already been created in OMS before the error occurred. Please verify in OMS
          before resubmitting to avoid creating duplicate orders. You'll be asked to confirm before this batch is resubmitted.
        </Alert>
      )}

      <Accordion expanded={expanded === "panel0"} onChange={() => setExpanded(expanded === "panel0" ? false : "panel0")}>
        <AccordionSummary expandIcon={<ArrowDropDownIcon />}>
          <Typography variant="h6">1. Material Details (Required)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box className="mb-5 mt-5" sx={{ backgroundColor: theme.palette.background.paper, boxShadow: theme.shadows[1], p: 2, borderRadius: "1.6rem", borderBottomRightRadius: 0, width: "100%" }}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={6} md={2.5}>
                <Typography variant="subtitle2" color="text.secondary" className="pb-8 flex"><Box component="span" sx={{ color: "error.main" }}>*</Box>Range</Typography>
                <Select value={formValues[0]?.range} onChange={(e) => handleRangeChange(e, 0, "range")} options={range} styles={customStyles()} className="w-full" />
              </Grid>
              <Grid item xs={12} sm={6} md={2.5}>
                <Typography variant="subtitle2" color="text.secondary" className="pb-8 flex"><Box component="span" sx={{ color: "error.main" }}>*</Box>Color</Typography>
                <Select value={formValues[0]?.color} onChange={(e) => handleRangeChange(e, 0, "color")} options={color[0]} styles={customStyles()} className="w-full" />
              </Grid>
              <Grid item xs={12} sm={6} md={2.5}>
                <Typography variant="subtitle2" color="text.secondary" className="pb-8 flex"><Box component="span" sx={{ color: "error.main" }}>*</Box>Carcass</Typography>
                <Select value={formValues[0]?.carcass} onChange={(e) => handleRangeChange(e, 0, "carcass")} options={carcass} styles={customStyles()} className="w-full" />
              </Grid>
              <Grid item xs={12} sm={6} md={2.5}>
                <Typography variant="subtitle2" color="text.secondary" className="pb-8 flex"><Box component="span" sx={{ color: "error.main" }}>*</Box>Handle</Typography>
                <Select value={formValues[0]?.handle} onChange={(e) => handleRangeChange(e, 0, "handle")} options={handles} styles={customStyles()} className="w-full" />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button variant="contained" color="secondary" fullWidth={isMobile} onClick={handleApplyMaterialInfo} disabled={!(formValues[0]?.range?.value && formValues[0]?.color?.value && formValues[0]?.carcass?.value && formValues[0]?.handle?.value && selectedRows.length > 0)} size="small">
                  Apply
                </Button>
              </Grid>
            </Grid>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion expanded={expanded === "panel1"} onChange={() => setExpanded(expanded === "panel1" ? false : "panel1")}>
        <AccordionSummary expandIcon={<ArrowDropDownIcon />}>
          <Box>
            <Typography variant="h6">2. Attachments</Typography>
            <Typography variant="caption" color="text.secondary">Select rows in the grid below first</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={1} sx={{ position: "relative" }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" className="pb-8 flex">DRW File</Typography>
                  <ColorButton variant="contained" component="label" disabled={loading}>
                    <FuseSvgIcon size={18} sx={{ display: "inline-block", mr: 1 }}>heroicons-outline:upload</FuseSvgIcon> Upload
                    <input type="file" className="file" key={key} onChange={(e) => handleFileUpload(e, 'DRW')} accept=".drw, .prw" />
                  </ColorButton>
                  <Button sx={{ mt: 1 }} size="small" color="error" onClick={() => handleRemoveFile('DRW')}>Remove from Selection</Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" className="pb-8 flex">Appliances Detail</Typography>
                  <ColorButton variant="contained" component="label" disabled={sharedLater || notApplicable || loading}>
                    <FuseSvgIcon size={18} sx={{ display: "inline-block", mr: 1 }}>heroicons-outline:upload</FuseSvgIcon> Upload
                    <input type="file" className="file" key={key} onChange={(e) => handleFileUpload(e, 'AD')} />
                  </ColorButton>
                  <Button sx={{ mt: 1 }} size="small" color="error" onClick={() => handleRemoveFile('AD')}>Remove from Selection</Button>
                </CardContent>
              </Card>
              <FormGroup>
                <Tooltip title="Checking this will uncheck 'NA' — only one can be active.">
                  <FormControlLabel control={<Checkbox checked={sharedLater} onChange={(e) => { setSharedLater(e.target.checked); setNotApplicable(false); }} />} label="Will be shared later" sx={{ "& span": { fontSize: { xs: "13px", sm: "11px" } } }} />
                </Tooltip>
                <Tooltip title="Checking this will uncheck 'Will be shared later' — only one can be active.">
                  <FormControlLabel control={<Checkbox checked={notApplicable} onChange={(e) => { setNotApplicable(e.target.checked); setSharedLater(false); }} />} label="NA" sx={{ "& span": { fontSize: { xs: "13px", sm: "11px" } } }} />
                </Tooltip>
              </FormGroup>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" className="pb-8 flex">Order Checklist</Typography>
                  <ColorButton variant="contained" component="label" disabled={drwFileSupersedes || loading}>
                    <FuseSvgIcon size={18} sx={{ display: "inline-block", mr: 1 }}>heroicons-outline:upload</FuseSvgIcon> Upload
                    <input type="file" className="file" key={key} onChange={(e) => handleFileUpload(e, 'CHECKLIST')} />
                  </ColorButton>
                  <Button sx={{ mt: 1 }} size="small" color="error" onClick={() => handleRemoveFile('CHECKLIST')}>Remove from Selection</Button>
                </CardContent>
              </Card>
              <FormGroup>
                <Tooltip title="When checked, the DRW file is treated as authoritative and supersedes the Order Checklist.">
                  <FormControlLabel control={<Checkbox checked={drwFileSupersedes} onChange={(e) => setDrwFileSupersedes(e.target.checked)} />} label="DRW file supersedes" sx={{ "& span": { fontSize: { xs: "13px", sm: "11px" } } }} />
                </Tooltip>
              </FormGroup>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ position: "relative", mb: 1 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" className="pb-8 flex">Drawing File</Typography>
                  <ColorButton variant="contained" component="label" disabled={loading}>
                    <FuseSvgIcon size={18} sx={{ display: "inline-block", mr: 1 }}>heroicons-outline:upload</FuseSvgIcon> Upload
                    <input type="file" className="file" key={key} onChange={(e) => handleFileUpload(e, 'DRAWING')} />
                  </ColorButton>
                  <Button sx={{ mt: 1 }} size="small" color="error" onClick={() => handleRemoveFile('DRAWING')}>Remove from Selection</Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Box sx={{ mt: 3, px: { xs: 1, sm: 2 }, mb: 5 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1, mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            3. Review Parts & Upload Mandatory Pictures
          </Typography>

          <Box display="flex" gap={1} sx={{ "& .MuiButton-root": { flex: { xs: 1, sm: "none" } } }}>
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={handleRemovePart}
              startIcon={<FuseSvgIcon size={18}>heroicons-outline:trash</FuseSvgIcon>}
            >
              Remove Part
            </Button>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              onClick={handleAddPart}
              startIcon={<FuseSvgIcon size={18}>heroicons-outline:plus</FuseSvgIcon>}
            >
              Add New Part
            </Button>
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          {!validationStatus.isMaterialCompleted && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              First select a Kitchen row, choose the correct Material Details above, and click Apply — required for every kitchen before you can submit.
            </Typography>
          )}
          {validationStatus.hasGridErrors && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: !validationStatus.isMaterialCompleted ? 0.5 : 0 }}>
              Rows with red-bordered cells need correction (missing reason, sub category, position number, or mandatory picture).
            </Typography>
          )}
        </Box>

        <div className={`h-full grid ${theme.palette.mode === "dark" ? "ag-theme-alpine-dark" : "ag-theme-alpine"}`} style={{ height: isMobile ? "65vh" : "600px", minHeight: isMobile ? 360 : undefined, width: "100%", marginTop: "8px" }}>
          <AgGridReact
            ref={gridRef}
            rowData={gridData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            autoGroupColumnDef={autoGroupColumnDef}
            rowGroupPanelShow={isMobile ? "never" : "always"}
            suppressDragLeaveHidesColumns={true}
            groupDefaultExpanded={-1}
            rowSelection="multiple"
            groupSelectsChildren={true}
            onSelectionChanged={onSelectionChanged}
            animateRows={true}
            wrapHeaderText={true}
            onGridReady={onGridReady}
            getRowId={(params) => params.data.__rowId}
            onCellValueChanged={handleCellValueChanged}
            onRowDataUpdated={handleRowDataUpdated}
          />
        </div>
      </Box>

      <Dialog open={confirmResubmitOpen} onClose={() => setConfirmResubmitOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Resubmit this order?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">
            The previous submission attempt failed, but some valid orders in this batch may have already been created
            in OMS. Resubmitting now will send the full batch again and could create duplicate orders for records that
            already succeeded.
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Please check OMS for this project before continuing. Only resubmit if you've confirmed no duplicates will
            be created.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmResubmitOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleConfirmResubmit}>Resubmit Anyway</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showResultDialog} onClose={closeResultDialog} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Submission Summary</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Successfully submitted <b>{progressStats.successful}</b> orders. Failed to submit <b>{progressStats.failed}</b> orders.
            Successful orders have been removed from the grid. You can review and retry the failed orders below.
          </Typography>
          <TableContainer component={Paper} elevation={0} variant="outlined">
            <Table size="small">
              <TableHead sx={{ backgroundColor: theme.palette.action.hover }}>
                <TableRow>
                  <TableCell><b>Unique ID</b></TableCell>
                  <TableCell><b>Status</b></TableCell>
                  <TableCell><b>Details</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {submissionResults.map((result, idx) => (
                  <TableRow key={idx}>
                    <TableCell sx={{ wordBreak: "break-word" }}>{result.uniqueId}</TableCell>
                    <TableCell>
                      <Chip
                        label={result.status === "fulfilled" ? "Success" : "Failed"}
                        size="small"
                        sx={{
                          backgroundColor: result.status === "fulfilled" ? theme.palette.success.dark : theme.palette.error.dark,
                          color: theme.palette.common.white,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ wordBreak: "break-word" }}>
                      {result.status === "fulfilled" ? "Order placed successfully." : result.reason}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeResultDialog} variant="contained" color="primary">
            Acknowledge & Continue
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop open={isSubmitting || loading} sx={{ zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <CircularProgress color="inherit" />
        {isSubmitting && progressStats.total > 0 && (
          <Box sx={{ width: '300px', maxWidth: '90vw', textAlign: 'center' }}>
            <Typography variant="h6" color="inherit" sx={{ mb: 1 }}>
              Processing {progressStats.completed} of {progressStats.total} Orders...
            </Typography>
            <LinearProgress variant="determinate" value={(progressStats.completed / progressStats.total) * 100} />
          </Box>
        )}
        {isSubmitting && progressStats.total === 0 && (
          <Typography variant="body2" color="inherit">Submitting your order — this can take a few seconds…</Typography>
        )}
      </Backdrop>
      <ProjectOrderSuccess
        open={openSuccessOrder}
        currentOrder={currentOrder}
        setOpenSuccessOrder={setOpenSuccessOrder}
      />
    </>
  );
}